import { useState, useRef, useCallback, useEffect } from 'react'
import { useDispatch } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import Icon from 'builder/components/Icon'
import { DashboardBuilder } from 'builder/components/Icon/24x24'
import { UserProfile } from 'builder/features/navigation/components/UserProfile/UserProfile'
import { useTypedSelector } from 'builder/hooks/useTypedSelector'
import { Pencil, CompactTextDisplay, CompactInput } from 'builder/components/EditorTitle/styles'
import LanguageSelect from 'builder/components/LanguageSelect'
import PENCIL from 'builder/components/EditorTitle/pencil.svg'
import { useI18n } from 'builder/hooks/useI18n'
import { ResumeEditorStore } from 'builder/modules/resumeEditor'
import { actions as careerProfileActions } from 'builder/modules/careerProfile'
import { DashboardButton } from '../Header/styles'
import {
  NavigationContainer,
  SidebarToggleButton,
  UserSettingsContainer,
  TabletTopRow,
  TabletTitleRow,
  TabletTitleCenter,
  TabletEditIcon,
  TabletLeftSection,
} from './styles'
import { useResumeNameChange } from './hooks'

type Props = {
  resumeState: ResumeEditorStore
  onSidebarToggle?: (isOpen: boolean) => void
  isSidebarOpen: boolean
  locales: Array<{ id: string; name: string }>
  onLocaleSelect: (locale: string) => void
}

export const NavigationHeaderTablet = (props: Props) => {
  const { resumeState, onSidebarToggle, isSidebarOpen, locales, onLocaleSelect } = props

  const dispatch = useDispatch()
  const navigate = useNavigate()
  const user = useTypedSelector(state => state.user.data)
  const isOnline = useTypedSelector(state => state.application.isOnline)
  const { i18n } = useI18n()

  const { resume } = resumeState

  const [isTabletEditing, setIsTabletEditing] = useState(false)
  const [tabletInputValue, setTabletInputValue] = useState(resume?.name || '')
  const tabletInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (!isTabletEditing) {
      setTabletInputValue(resume?.name || '')
    }
  }, [resume?.name, isTabletEditing])

  const { handleResumeNameChange } = useResumeNameChange()

  const handleSidebarToggle = () => {
    onSidebarToggle?.(!isSidebarOpen)
  }

  const handleTabletEditClick = useCallback(() => {
    setIsTabletEditing(true)
    setTabletInputValue(resume?.name || '')
    setTimeout(() => {
      if (tabletInputRef.current) {
        tabletInputRef.current.focus()
        tabletInputRef.current.select()
      }
    }, 0)
  }, [resume?.name])

  const handleTabletInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setTabletInputValue(e.target.value)
  }, [])

  const handleTabletInputBlur = useCallback(() => {
    setIsTabletEditing(false)
    const trimmedValue = tabletInputValue.trim()
    if (trimmedValue !== resume?.name) {
      handleResumeNameChange(trimmedValue)
    }
  }, [tabletInputValue, resume?.name, handleResumeNameChange])

  const handleTabletInputKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        if (tabletInputRef.current) {
          tabletInputRef.current.blur()
        }
      }
      if (e.key === 'Escape') {
        setTabletInputValue(resume?.name || '')
        setIsTabletEditing(false)
      }
    },
    [resume?.name],
  )

  const handleDashboardClick = () => {
    navigate('/')
  }

  const openMobileProfileMenu = () => {
    dispatch(careerProfileActions.setMobileProfileMenu(true))
  }

  return (
    <NavigationContainer>
      <TabletTopRow>
        <TabletLeftSection>
          <SidebarToggleButton
            onClick={handleSidebarToggle}
            data-testid="toggle-sidebar-navigation-button"
          >
            {isSidebarOpen ? <Icon.CloseLarge /> : <DashboardBuilder />}
          </SidebarToggleButton>
          <DashboardButton
            onClick={handleDashboardClick}
            data-testid="navigate-to-dashboard-button"
          >
            {i18n.t('builder.side_menu.dashboard')}
          </DashboardButton>
        </TabletLeftSection>
        {isOnline && user && (
          <UserSettingsContainer onClick={openMobileProfileMenu}>
            <UserProfile showOnlyAvatar={true} />
          </UserSettingsContainer>
        )}
      </TabletTopRow>

      <TabletTitleRow>
        <TabletTitleCenter>
          {isTabletEditing ? (
            <>
              <CompactInput
                ref={tabletInputRef}
                value={tabletInputValue}
                onChange={handleTabletInputChange}
                onBlur={handleTabletInputBlur}
                onKeyDown={handleTabletInputKeyDown}
                placeholder={i18n.t('builder.resume_editor.untitled')}
              />
              <LanguageSelect
                selected={resume?.locale || 'en'}
                onSelect={onLocaleSelect}
                options={locales}
              />
            </>
          ) : (
            <>
              <CompactTextDisplay $isHovered={false}>
                {resume?.name ? resume.name : i18n.t('builder.resume_editor.untitled')}
              </CompactTextDisplay>
              <LanguageSelect
                selected={resume?.locale || 'en'}
                onSelect={onLocaleSelect}
                options={locales}
              />
            </>
          )}
        </TabletTitleCenter>
        <TabletEditIcon>
          <Pencil onClick={handleTabletEditClick} $icon={PENCIL} />
        </TabletEditIcon>
      </TabletTitleRow>
    </NavigationContainer>
  )
}
